import { useForceRerender, useValueEqualMemo } from "client/Hooks";
import { Rect } from "client/Misc/commonTypes";
import {
	getPropertyPathResolver,
	isNotNil,
	throttle
} from "client/Misc/utils";
import { newGuid } from "client_legacy/ndaw/guid";
import {
	ReactElement,
	ReadonlyRefObject,
	Ref,
	forwardRef,
	useCallback,
	useImperativeHandle,
	useLayoutEffect,
	useMemo,
	useRef,
	useState
} from "react";
import { VisibleAreaTracker } from "../VisibleAreaTracker/VisibleAreaTracker";
import { Item } from "./Item";
import { LoadMoreIndicator } from "./LoadMoreIndicator";
import { MobilePlusMenuSpacing } from "./MobilePlusMenuSpacing";
import { EndOfListSpacing } from "./EndOfListSpacing";

const afterKey: string = `__AFTER_ITEMS__${newGuid()}`;
const beforeKey: string = `__BEFORE_ITEMS__${newGuid()}`;

export type VirtualListHandle<TItem> = {
	getItemPosition: (item: TItem) => VirtualListVerticalSizeAndPositionInformation;
};

export type VirtualListProps<TItem> = {
	items: readonly TItem[];
	children(item: TItem, index: number): ReactElement;
	itemKey:
		| PropertyPath<TItem, string | number>
		| ((item: TItem) => string | number);
	getIsPrerender?(item: TItem): boolean;
	getStaticHeight?(item: TItem): number | undefined;
	trackingId: string;
} & ({
	itemHeight: number;
	estimatedHeightOfAnItem?: undefined;
	renderAfterItems?: undefined;
	renderBeforeItems?: undefined;
} | {
	estimatedHeightOfAnItem: number;
	itemHeight?: undefined;
	renderAfterItems?: ReactElement | null;
	renderBeforeItems?: ReactElement | null;
});

const VirtualListInternal = forwardRef(
	<TItem,>(
		props: VirtualListProps<TItem>,
		ref: Ref<VirtualListHandle<TItem>>
	): ReactElement => {
		const {
			itemKey,
			children: renderItem,
			getIsPrerender,
			getStaticHeight,
			trackingId,
			itemHeight,
			estimatedHeightOfAnItem,
			items,
			renderAfterItems,
			renderBeforeItems
		} = props;

		const [visibleArea, setVisibleArea] = useState<Rect>({
			left: 0,
			top: 0,
			right: 0,
			bottom: 0
		});

		const [alwaysVisibleItemKeys, setAlwaysVisibleItemKeys] = useState<ReadonlySet<string>>(new Set());
		const [measuredHeights, setMeasuredHeights] = useState<Map<string, number>>(new Map());

		const [notifyAboutHeightChanges, immediateHeightChangesVersion] = useForceRerender();
		const unprocessedHeightChanges: ReadonlyRefObject<ItemChange<number>[]> = useRef<ItemChange<number>[]>([]);

		const hasComponentToRenderAfterItems: boolean = isNotNil(renderAfterItems);
		const hasComponentToRenderBeforeItems: boolean = isNotNil(renderBeforeItems);

		const getUniqueKey = useMemo(
			(): ((item: TItem) => string) => {
				const resolvedItemKey: (item: TItem) => string | number = typeof itemKey === "string"
					? getPropertyPathResolver(itemKey)
					: itemKey;

				return (item) => formatKey(resolvedItemKey(item));
			},
			[itemKey]
		);

		const getChildInfoAndSearchKey = useCallback(
			(item: TItem): [ChildInfo, string] => {
				const uniqueKey: string = getUniqueKey(item);
				const childInfo: ChildInfo = {
					key: uniqueKey,
					isPrerender: isNotNil(getIsPrerender) && getIsPrerender(item),
					staticHeight: isNotNil(getStaticHeight)
						? getStaticHeight(item)
						: itemHeight
				};

				return [childInfo, uniqueKey];
			},
			[
				getIsPrerender,
				getStaticHeight,
				getUniqueKey,
				itemHeight
			]
		);

		const [childInfos, childSearchKeyIndex] = useMemo(
			(): [ReadonlyArray<ChildInfo>, Map<string, number>] => {
				const childInfos: ChildInfo[] = [];
				childInfos.length = items.length
					+ Number(hasComponentToRenderAfterItems)
					+ Number(hasComponentToRenderBeforeItems);

				const childSearchKeyIndex = new Map<string, number>();
				let itemIndex: number = 0;

				if (hasComponentToRenderBeforeItems) {
					childInfos[itemIndex] = {
						isPrerender: false,
						key: beforeKey,
						staticHeight: undefined
					};

					itemIndex++;
				}
				for (let i = 0; i < items.length; i++) {
					const [childInfo, searchKey] = getChildInfoAndSearchKey(items[i]);
					childInfos[itemIndex] = childInfo;
					childSearchKeyIndex.set(searchKey, itemIndex);

					itemIndex++;
				}

				if (hasComponentToRenderAfterItems) {
					childInfos[itemIndex] = {
						isPrerender: false,
						key: afterKey,
						staticHeight: undefined
					};
				}

				return [childInfos, childSearchKeyIndex];
			},
			[
				items,
				hasComponentToRenderBeforeItems,
				hasComponentToRenderAfterItems,
				getChildInfoAndSearchKey
			]
		);

		const getItemHeight = useCallback(
			(itemIndex: number): number => {
				const {
					key,
					staticHeight
				} = childInfos[itemIndex];

				if (typeof staticHeight === "number") {
					return staticHeight;
				}

				const immediateResult: ItemChange<number> | undefined = unprocessedHeightChanges.current.find(c => c.itemKey === key);
				if (immediateResult) {
					return immediateResult.value;
				}

				const measuredResult: number | undefined = measuredHeights.get(key);
				if (typeof measuredResult === "number") {
					return measuredResult;
				}

				if (typeof estimatedHeightOfAnItem === "undefined") {
					throw new Error(`No height exist for item on index '${itemIndex}'`);
				}

				return estimatedHeightOfAnItem;
			},
			[
				childInfos,
				estimatedHeightOfAnItem,
				measuredHeights
			]
		);

		const [childVerticalSizeAndPositionInformations, totalListHeight] = useMemo(
			(): [ReadonlyArray<VirtualListVerticalSizeAndPositionInformation>, number] => {
				const result: VirtualListVerticalSizeAndPositionInformation[] = [];
				result.length = childInfos.length;

				let top: number = 0;
				let prerenderTop: number = visibleArea.top;

				for (let i = 0; i < childInfos.length; i++) {
					const { isPrerender } = childInfos[i];
					const height: number = getItemHeight(i);
					const renderTop: number = isPrerender ? prerenderTop : top;
					const renderHeight: number = isPrerender ? 0 : height;

					result[i] = {
						top: renderTop,
						height: renderHeight
					};

					if (isPrerender) {
						prerenderTop += height;
					} else {
						top += height;
					}
				}

				return [result, top];
			},
			[
				childInfos,
				getItemHeight,
				visibleArea.top
			]
		);

		const visibilityMargin: number = (estimatedHeightOfAnItem ?? itemHeight) * 2;

		const visibleChildrenIndexes = useMemo(
			(): ReadonlyArray<number> => {
				const visibleChildrenIndexes: number[] = [];

				for (let i = 0; i < childVerticalSizeAndPositionInformations.length; i++) {
					const { top, height } = childVerticalSizeAndPositionInformations[i];
					const bottom: number = top + height;

					if (bottom >= visibleArea.top - visibilityMargin && top <= visibleArea.bottom + visibilityMargin) {
						visibleChildrenIndexes.push(i);
					} else {
						const { key: itemKey } = childInfos[i];

						if (alwaysVisibleItemKeys.has(itemKey)) {
							visibleChildrenIndexes.push(i);
						}
					}
				}

				return visibleChildrenIndexes;
			},
			[
				alwaysVisibleItemKeys,
				childInfos,
				childVerticalSizeAndPositionInformations,
				visibilityMargin,
				visibleArea.bottom,
				visibleArea.top
			]
		);

		const stableVisibleChildrenIndexes = useValueEqualMemo(
			(): readonly number[] => visibleChildrenIndexes,
			[visibleChildrenIndexes]
		);

		const visibleKeys: ReadonlyArray<string> = useMemo(
			(): ReadonlyArray<string> => {
				return stableVisibleChildrenIndexes.map(globalIndex => childInfos[globalIndex].key);
			},
			[childInfos, stableVisibleChildrenIndexes]
		);

		const throttledNotifyAboutHeightChanges = useMemo(
			(): (() => void) => {
				return throttle(notifyAboutHeightChanges, 50);
			},
			[notifyAboutHeightChanges]
		);

		const heightChangedHandler = useCallback(
			(itemKey: string, height: number): void => {
				if (visibleKeys.indexOf(itemKey) !== -1) {
					unprocessedHeightChanges.current.push({ itemKey, value: height });
					throttledNotifyAboutHeightChanges();
				}
			},
			[throttledNotifyAboutHeightChanges, visibleKeys]
		);

		const isAlwaysVisibleChangeHandler = useCallback(
			(itemKey: string, isAlwaysVisible: boolean): void => {
				setAlwaysVisibleItemKeys(
					alwaysVisibleItemKeys => {
						const didChange: boolean = isAlwaysVisible !== alwaysVisibleItemKeys.has(itemKey);
						if (!didChange) {
							return alwaysVisibleItemKeys;
						}

						const copy = new Set(alwaysVisibleItemKeys);

						if (isAlwaysVisible) {
							copy.add(itemKey);
						} else {
							copy.delete(itemKey);
						}

						return copy;
					}
				);
			},
			[]
		);

		useLayoutEffect(
			() => {
				if (unprocessedHeightChanges.current.length === 0) {
					return;
				}

				const changesToPersist: ItemChange<number>[] = [...unprocessedHeightChanges.current];
				unprocessedHeightChanges.current.length = 0;

				setMeasuredHeights(measuredHeights => {
					const result = new Map(measuredHeights);
					let hasAnyChanges: boolean = false;

					for (const heightChange of changesToPersist) {
						if (result.get(heightChange.itemKey) !== heightChange.value) {
							result.set(heightChange.itemKey, heightChange.value);
							hasAnyChanges = true;
						}
					}

					return hasAnyChanges ? result : measuredHeights;
				});
			},
			[immediateHeightChangesVersion]
		);

		useImperativeHandle(
			ref,
			(): VirtualListHandle<TItem> => ({
				getItemPosition(item: TItem): VirtualListVerticalSizeAndPositionInformation {
					const searchKey: string = getUniqueKey(item);
					const index: number | undefined = childSearchKeyIndex.get(searchKey);

					if (typeof index !== "number") {
						throw new Error(`Unknown search key: ${searchKey}`);
					}

					return childVerticalSizeAndPositionInformations[index];
				}
			}),
			[
				childSearchKeyIndex,
				childVerticalSizeAndPositionInformations,
				getUniqueKey
			]
		);

		const itemIndexOffset: number = -Number(hasComponentToRenderBeforeItems);

		return (
			<VisibleAreaTracker
				direction="vertical"
				height={totalListHeight}
				onChange={setVisibleArea}
				trackingId={trackingId}
			>
				{stableVisibleChildrenIndexes.map((globalIndex): ReactElement => {
					const childInfo: ChildInfo = childInfos[globalIndex];
					const sizeAndPositionInfo: VirtualListVerticalSizeAndPositionInformation = childVerticalSizeAndPositionInformations[globalIndex];

					return (
						<Item
							key={childInfo.key}
							isPrerender={childInfo.isPrerender}
							itemKey={childInfo.key}
							topAndHeight={sizeAndPositionInfo}
							shouldMeasureHeight={typeof childInfo.staticHeight !== "number"}
							onHeightChanged={heightChangedHandler}
							onIsAlwaysVisibleChange={isAlwaysVisibleChangeHandler}
						>
							{childInfos[globalIndex].key === afterKey
								? renderAfterItems
								: childInfos[globalIndex].key === beforeKey
									? renderBeforeItems
									: renderItem(
										items[globalIndex + itemIndexOffset],
										globalIndex
									)
							}
						</Item>
					);
				})}
			</VisibleAreaTracker>
		);
	}
);
VirtualListInternal.displayName = "VirtualList";

export const VirtualList = VirtualListInternal as unknown as {
	<TItem>(props: VirtualListProps<TItem> & { ref?: Ref<VirtualListHandle<TItem>>; }): ReactElement;
	displayName: string;
	LoadMoreIndicator: typeof LoadMoreIndicator;
	MobilePlusMenuSpacing: typeof MobilePlusMenuSpacing;
	EndOfListSpacing: typeof EndOfListSpacing;
};

VirtualList.LoadMoreIndicator = LoadMoreIndicator;
VirtualList.MobilePlusMenuSpacing = MobilePlusMenuSpacing;
VirtualList.EndOfListSpacing = EndOfListSpacing;

type ItemChange<TValue> = {
	itemKey: string;
	value: TValue;
};

type ChildInfo = {
	key: string;
	isPrerender: boolean;
	staticHeight: number | undefined;
};

function formatKey(unformattedKey: string | number): string {
	if (typeof unformattedKey === "number") {
		return `number-key_${unformattedKey}`;
	}

	return unformattedKey;
}

export type VirtualListVerticalSizeAndPositionInformation = {
	top: number;
	height: number;
};